import { z } from 'zod';
/**
 * Vehicle classes — the cohorts that make comparison between vehicles possible.
 *
 * TENANT-DEFINED, not a platform enum. A fixed list (TRUCK/VAN/BUS) cannot
 * serve a customer base whose fleets do not split that way, and it would still
 * put a 3.5-tonne van and a 7.5-tonne box truck in one cohort — vehicles with
 * different normal coolant temperatures, fuel rates and engine loads. Each
 * operator names the classes their fleet actually has.
 *
 * Referenced by ID rather than by name. "Truck", "truck" and "HGV" are three
 * cohorts, silently, and a comparison that splits its own cohorts on
 * capitalisation is worse than no comparison at all.
 */
export const vehicleClassSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    archived: z.boolean(),
    /**
     * Vehicles assigned, ARCHIVED VEHICLES INCLUDED — they are still in the
     * cohort and still in its history. A count that hid them would make an
     * apparently empty class dangerous to archive.
     */
    vehicleCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const vehicleClassListSchema = z.object({ items: z.array(vehicleClassSchema) });
export const vehicleClassListQuerySchema = z
    .object({
    /**
     * `z.enum` rather than `z.coerce.boolean()` — risk R8: coercion makes the
     * STRING "false" truthy, so `?includeArchived=false` would include them.
     */
    includeArchived: z.enum(['true', 'false']).optional(),
})
    .strict();
const name = z
    .string()
    .trim()
    .min(1)
    .max(60)
    // Trimmed and length-bounded, but otherwise free: an operator's own
    // vocabulary is the whole point, and imposing a format would push them back
    // toward one generic class.
    .describe('Name of the class, unique within the tenant');
export const createVehicleClassRequestSchema = z
    .object({
    name,
    description: z.string().trim().max(500).nullable().optional(),
})
    .strict();
export const updateVehicleClassRequestSchema = z
    .object({
    name: name.optional(),
    description: z.string().trim().max(500).nullable().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be supplied' });
//# sourceMappingURL=vehicle-class.js.map